import { useRef, useState, type ComponentType, type ReactNode } from "react";
import { Link, NavLink } from "react-router";
import type { AttachmentOrigin } from "@/domain/attachments";
import { ImageInput } from "./ImageCapture";
import {
  IconArrowDown,
  IconArrowUp,
  IconCalendar,
  IconCamera,
  IconChart,
  IconHome,
  IconImage,
  IconMore,
  IconPen,
  IconPlus,
  IconScale,
  IconSettings,
  IconSync,
  IconTable,
  IconTags,
  IconTarget,
  IconUpload,
  type IconProps,
} from "./icons";
import { Notice } from "./primitives";

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<IconProps>;
  end?: boolean;
}

/** The sections, in the order they appear in the desktop rail and the "Altro" sheet. */
export const SECTIONS: NavItem[] = [
  { to: "/", label: "Riepilogo", icon: IconHome, end: true },
  { to: "/mese", label: "Mese", icon: IconCalendar },
  { to: "/anno", label: "Anno", icon: IconChart },
  { to: "/saldo", label: "Saldo", icon: IconScale },
  { to: "/budget", label: "Budget", icon: IconTarget },
  { to: "/spese", label: "Spese", icon: IconArrowUp },
  { to: "/entrate", label: "Entrate", icon: IconArrowDown },
  { to: "/categorie", label: "Categorie", icon: IconTags },
  { to: "/importa", label: "Importa", icon: IconUpload },
  { to: "/sync", label: "Sincronizza", icon: IconSync },
  { to: "/excel", label: "Copia per Excel", icon: IconTable },
  { to: "/impostazioni", label: "Impostazioni", icon: IconSettings },
];

const MOBILE_TABS = ["/", "/mese", "/spese"];

function Sheet({ id, title, dialogRef, children }: { id: string; title: string; dialogRef: React.RefObject<HTMLDialogElement | null>; children: ReactNode }) {
  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={id}
      onClick={(event) => {
        if (event.target === dialogRef.current) dialogRef.current?.close();
      }}
      className="sheet fixed inset-x-0 top-auto bottom-0 m-0 w-full max-w-none rounded-t-2xl bg-surface p-0 text-ink md:inset-0 md:m-auto md:h-fit md:max-w-md md:rounded-2xl"
    >
      <div className="safe-bottom px-5 pt-3">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-line md:hidden" aria-hidden="true" />
        <h2 id={id} className="text-lg font-semibold">
          {title}
        </h2>
        {children}
        <button type="button" onClick={() => dialogRef.current?.close()} className="mt-2 mb-2 w-full rounded-lg py-3 font-medium text-muted hover:bg-ground">
          Chiudi
        </button>
      </div>
    </dialog>
  );
}

type AddChoice =
  | { kind: "link"; to: string; title: string; detail: string; icon: ComponentType<IconProps> }
  | { kind: "image"; origin: AttachmentOrigin; title: string; detail: string; icon: ComponentType<IconProps> };

/** Receives the chosen image; throws with a user-facing message when the image cannot be used. */
export type CaptureImageHandler = (file: File, origin: AttachmentOrigin) => Promise<void>;

const ADD_CHOICES: AddChoice[] = [
  { kind: "link", to: "/spese/nuova", title: "Manuale", detail: "Importo, data, metodo di pagamento e categoria", icon: IconPen },
  { kind: "image", origin: "camera", title: "Fotografa ricevuta", detail: "Scatta una foto dello scontrino, poi completa la spesa", icon: IconCamera },
  { kind: "image", origin: "library", title: "Carica screenshot / foto", detail: "Scegli un'immagine già presente sul telefono", icon: IconImage },
  { kind: "link", to: "/entrate/nuova", title: "Entrata", detail: "Stipendio, rimborso, regalo…", icon: IconArrowDown },
];

function ChoiceBody({ icon: Icon, title, detail }: { icon: ComponentType<IconProps>; title: string; detail: string }) {
  return (
    <>
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-petrol-soft text-petrol">
        <Icon />
      </span>
      <span className="flex flex-col">
        <span className="font-medium">{title}</span>
        <span className="text-sm text-muted">{detail}</span>
      </span>
    </>
  );
}

function AddButton({ variant, onCaptureImage }: { variant: "fab" | "rail"; onCaptureImage: CaptureImageHandler }) {
  const sheet = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = () => {
    setError(null);
    sheet.current?.showModal();
  };
  async function capture(file: File, origin: AttachmentOrigin) {
    setError(null);
    setBusy(true);
    try {
      await onCaptureImage(file, origin);
      sheet.current?.close();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      {variant === "fab" ? (
        <button
          type="button"
          onClick={open}
          aria-label="Aggiungi"
          className="-mt-7 flex size-16 shrink-0 items-center justify-center rounded-full bg-petrol text-white shadow-[0_6px_16px_rgb(15_85_99/0.35)] active:scale-95"
        >
          <IconPlus width={28} height={28} />
        </button>
      ) : (
        <button type="button" onClick={open} className="flex w-full items-center justify-center gap-2 rounded-lg bg-petrol px-4 py-3 font-semibold text-white hover:bg-petrol-strong">
          <IconPlus />
          Aggiungi
        </button>
      )}
      <Sheet id={`add-${variant}`} title="Aggiungi spesa" dialogRef={sheet}>
        <ul className="mt-3 divide-y divide-line">
          {ADD_CHOICES.map((choice) => (
            <li key={choice.title}>
              {choice.kind === "link" ? (
                <Link to={choice.to} onClick={() => sheet.current?.close()} className="flex min-h-16 items-center gap-4 py-3">
                  <ChoiceBody {...choice} />
                </Link>
              ) : (
                <ImageInput
                  origin={choice.origin}
                  onFile={capture}
                  disabled={busy}
                  testId={`add-image-${choice.origin}`}
                  className="flex min-h-16 items-center gap-4 py-3 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-petrol"
                >
                  <ChoiceBody {...choice} />
                </ImageInput>
              )}
            </li>
          ))}
        </ul>
        <div aria-live="polite">
          {busy ? <p className="py-2 text-sm text-muted">Preparazione dell&apos;immagine…</p> : null}
          {error ? <Notice tone="error">{error}</Notice> : null}
        </div>
      </Sheet>
    </>
  );
}

function MoreButton() {
  const sheet = useRef<HTMLDialogElement>(null);
  const others = SECTIONS.filter((s) => !MOBILE_TABS.includes(s.to));
  return (
    <>
      <button type="button" onClick={() => sheet.current?.showModal()} className="flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 text-[0.7rem] text-muted">
        <IconMore />
        Altro
      </button>
      <Sheet id="more-sheet" title="Sezioni" dialogRef={sheet}>
        <ul className="mt-3 grid grid-cols-2 gap-2">
          {others.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                onClick={() => sheet.current?.close()}
                className={({ isActive }) =>
                  `flex min-h-12 items-center gap-3 rounded-lg px-3 py-2 ${isActive ? "bg-petrol-soft font-semibold text-petrol-strong" : "bg-ground"}`
                }
              >
                <Icon />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </Sheet>
    </>
  );
}

function Tab({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 text-[0.7rem] ${isActive ? "font-semibold text-petrol" : "text-muted"}`
      }
    >
      <Icon />
      {item.label}
    </NavLink>
  );
}

export function AppShell({ children, banner, onCaptureImage }: { children: ReactNode; banner?: ReactNode; onCaptureImage: CaptureImageHandler }) {
  const tabs = SECTIONS.filter((s) => MOBILE_TABS.includes(s.to));
  return (
    <div className="min-h-dvh md:grid md:grid-cols-[15rem_1fr]">
      <aside className="sticky top-0 hidden h-dvh flex-col gap-5 overflow-y-auto border-r border-line bg-surface px-4 py-6 md:flex">
        <Link to="/" className="px-3 font-ledger text-2xl text-petrol-strong">
          Bilancio
        </Link>
        <AddButton variant="rail" onCaptureImage={onCaptureImage} />
        <nav aria-label="Sezioni">
          <ul className="flex flex-col gap-0.5">
            {SECTIONS.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-[0.95rem] ${
                      isActive ? "bg-petrol-soft font-semibold text-petrol-strong" : "text-muted hover:bg-ground hover:text-ink"
                    }`
                  }
                >
                  <Icon />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <main className="mx-auto w-full max-w-4xl px-4 pt-6 pb-32 md:px-10 md:pt-10 md:pb-12">
        {banner}
        {children}
      </main>

      <nav aria-label="Sezioni principali" className="safe-bottom fixed inset-x-0 bottom-0 z-10 flex items-end border-t border-line bg-surface/95 px-2 pt-1 backdrop-blur md:hidden">
        <Tab item={tabs[0]!} />
        <Tab item={tabs[1]!} />
        <div className="flex flex-1 justify-center">
          <AddButton variant="fab" onCaptureImage={onCaptureImage} />
        </div>
        <Tab item={tabs[2]!} />
        <MoreButton />
      </nav>
    </div>
  );
}
